import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Search,
  MoreVertical,
  Menu,
  Maximize2,
  ImagePlus,
  Video,
} from "lucide-react";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.magnirauniverse.com";

const progressOptions = [0, 25, 50, 75, 100];

const SiteProgress = ({ project }) => {
  const [items, setItems] = useState([]);
  const [activeRoom, setActiveRoom] = useState("All");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [progressFilter, setProgressFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedUploadItem, setSelectedUploadItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const loginUser =
    JSON.parse(localStorage.getItem("user"))?.name ||
    localStorage.getItem("name") ||
    "";

  const getFileUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${BASE_URL.replace("/api", "")}${url}`;
  };

  const fetchSiteProgress = async () => {
    if (!project?.name) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/site-progress`, {
        params: {
          projectName: project.name,
          projectId: project._id || project.id || "",
        },
      });

      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (error) {
      console.error("Site progress fetch error:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSiteProgress();
  }, [project]);

  const updateItemInState = (updatedItem) => {
    setItems((prev) =>
      prev.map((oldItem) =>
        oldItem._id === updatedItem._id ? updatedItem : oldItem,
      ),
    );

    setSelectedItem((prev) =>
      prev?._id === updatedItem._id ? updatedItem : prev,
    );
  };

  const handleProgressChange = async (item, value) => {
    const newProgress = Number(value);
    const currentProgress = Number(item.progress || 0);

    if (newProgress < currentProgress) {
      alert(
        `Progress cannot go back. Current progress is ${currentProgress}%.`,
      );
      return;
    }

    try {
      const res = await axios.put(
        `${BASE_URL}/api/site-progress/${encodeURIComponent(
          project.name,
        )}/items/${item._id}`,
        {
          progress: newProgress,
          updatedBy: loginUser,
        },
      );

      updateItemInState(res.data.item);
    } catch (error) {
      console.error("Progress update error:", error);
      alert(error.response?.data?.message || "Progress update failed");
      fetchSiteProgress();
    }
  };

  const handleDrawerSliderChange = (value) => {
    const newValue = Number(value);
    const oldValue = Number(selectedItem?.progress || 0);

    if (newValue < oldValue) return;

    setSelectedItem((prev) => ({
      ...prev,
      progress: newValue,
    }));
  };

  const handleDrawerSliderSave = (value) => {
    if (!selectedItem) return;
    handleProgressChange(selectedItem, value);
  };

  const handleFileUpload = async (e, uploadType) => {
    const file = e.target.files?.[0];

    if (!file || !selectedUploadItem) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadedBy", loginUser);

    try {
      const res = await axios.post(
        `${BASE_URL}/api/site-progress/${encodeURIComponent(
          project.name,
        )}/items/${selectedUploadItem._id}/upload`,
        formData,
      );

      updateItemInState(res.data.item);
      alert(`${uploadType} uploaded successfully`);
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Upload failed");
    } finally {
      e.target.value = "";
      setOpenMenuId(null);
      setSelectedUploadItem(null);
    }
  };

  const roomGroups = useMemo(() => {
    const grouped = {};

    items.forEach((item) => {
      const room = item.room || "Unsectioned";
      if (!grouped[room]) grouped[room] = [];
      grouped[room].push(item);
    });

    return grouped;
  }, [items]);

  const roomTabs = useMemo(() => Object.keys(roomGroups), [roomGroups]);

  const filteredItemsByRoom = useMemo(() => {
    const selectedRooms =
      activeRoom === "All" ? Object.keys(roomGroups) : [activeRoom];

    const result = {};

    selectedRooms.forEach((room) => {
      const roomItems = roomGroups[room] || [];

      result[room] = roomItems.filter((item) => {
        const searchText =
          `${item.name} ${item.description} ${item.category} ${item.code}`
            .toLowerCase()
            .trim();

        return (
          searchText.includes(search.toLowerCase()) &&
          (categoryFilter ? item.category === categoryFilter : true) &&
          (statusFilter ? item.status === statusFilter : true) &&
          (typeFilter ? item.itemType === typeFilter : true) &&
          (progressFilter
            ? String(item.progress) === String(progressFilter)
            : true)
        );
      });
    });

    return result;
  }, [
    activeRoom,
    roomGroups,
    search,
    categoryFilter,
    statusFilter,
    typeFilter,
    progressFilter,
  ]);

  const getSectionProgress = (roomItems = []) => {
    if (!roomItems.length) return 0;

    const total = roomItems.reduce(
      (sum, item) => sum + Number(item.progress || 0),
      0,
    );

    return Math.round(total / roomItems.length);
  };

  const getDateTime = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const uniqueValues = (key) => {
    return [...new Set(items.map((item) => item[key]).filter(Boolean))];
  };

  const openUpload = (item, type) => {
    setSelectedUploadItem(item);

    if (type === "photo") {
      photoInputRef.current?.click();
    } else {
      videoInputRef.current?.click();
    }
  };

  return (
    <div style={styles.page}>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => handleFileUpload(e, "Photo")}
      />

      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        style={{ display: "none" }}
        onChange={(e) => handleFileUpload(e, "Video")}
      />

      <div style={styles.header}>
        <div>
          <button style={styles.backBtn} onClick={() => window.history.back()}>
            ← Back
          </button>
          <h2 style={styles.title}>Work Progress</h2>
          <p style={styles.projectName}>
            Project: <b>{project?.name || "Selected Project"}</b>
          </p>
        </div>

        <button style={styles.actionBtn}>Actions</button>
      </div>

      <div style={styles.topTabs}>
        <button style={{ ...styles.topTab, ...styles.activeTopTab }}>
          Scope Update
        </button>
        <button style={styles.topTab}>Progress Report</button>
      </div>

      <div style={styles.card}>
        <div style={styles.roomTabWrapper}>
          <Menu size={18} color="#777" />

          <button
            onClick={() => setActiveRoom("All")}
            style={{
              ...styles.roomTab,
              ...(activeRoom === "All" ? styles.activeRoomTab : {}),
            }}
          >
            All <span style={styles.count}>{items.length}</span>
          </button>

          {roomTabs.map((room) => (
            <button
              key={room}
              onClick={() => setActiveRoom(room)}
              style={{
                ...styles.roomTab,
                ...(activeRoom === room ? styles.activeRoomTab : {}),
              }}
            >
              {room} <span style={styles.count}>{roomGroups[room].length}</span>
            </button>
          ))}

          <Maximize2 size={17} color="#333" />
        </div>

        <div style={styles.tableHeader}>
          <div></div>
          <div>S.No</div>
          <div>Item Name & Description</div>
          <div>Category</div>
          <div>Status</div>
          <div>Item Type</div>
          <div>UOM</div>
          <div>Quantity</div>
          <div>Progress</div>
        </div>

        <div style={styles.filterRow}>
          <div>
            <input type="checkbox" />
          </div>

          <div>
            <Search size={17} color="#aaa" />
          </div>

          <div>
            <input
              style={styles.input}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div>
            <select
              style={styles.select}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">Select</option>
              {uniqueValues("category").map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              style={styles.select}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Select</option>
              {uniqueValues("status").map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              style={styles.select}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">Select</option>
              {uniqueValues("itemType").map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </div>

          <div>
            <select style={styles.select}>
              <option>Select</option>
            </select>
          </div>

          <div>
            <input style={styles.input} placeholder="Search" />
          </div>

          <div>
            <select
              style={styles.select}
              value={progressFilter}
              onChange={(e) => setProgressFilter(e.target.value)}
            >
              <option value="">Select</option>
              {progressOptions.map((p) => (
                <option key={p} value={p}>
                  {p}%
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div style={styles.empty}>Loading site progress...</div>
        ) : items.length === 0 ? (
          <div style={styles.empty}>
            No approved quote items found for this project.
          </div>
        ) : (
          Object.entries(filteredItemsByRoom).map(([room, roomItems]) => (
            <div key={room}>
              <div style={styles.sectionRow}>
                <div>
                  {room} <span style={styles.count}>{roomItems.length}</span>
                </div>
                <div>Section Progress: {getSectionProgress(roomItems)}%</div>
              </div>

              {roomItems.map((item) => (
                <div
                  key={item._id}
                  style={styles.itemRow}
                  onClick={() => setSelectedItem(item)}
                >
                  <div>
                    <input
                      type="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  <div>
                    <b>{item.srNo}.</b>
                  </div>

                  <div>
                    <div style={styles.itemName}>{item.name}</div>
                    <div style={styles.desc}>{item.description}</div>
                  </div>

                  <div>
                    <div>{item.category}</div>
                    <div style={styles.code}>{item.code}</div>
                  </div>

                  <div>
                    <span
                      style={{
                        ...styles.statusBadge,
                        background:
                          item.status === "Completed"
                            ? "#dcfce7"
                            : item.status === "In Progress"
                              ? "#fef9c3"
                              : "#f1f5f9",
                        color:
                          item.status === "Completed"
                            ? "#166534"
                            : item.status === "In Progress"
                              ? "#854d0e"
                              : "#475569",
                      }}
                    >
                      {item.status}
                    </span>
                  </div>

                  <div>
                    <span style={styles.typeBadge}>{item.itemType}</span>
                  </div>

                  <div>{item.uom}</div>

                  <div>{item.quantity}</div>

                  <div>
                    <div style={styles.progressTop}>
                      <span>Progress Status: {item.progress}%</span>

                      <select
                        value={item.progress}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          handleProgressChange(item, e.target.value)
                        }
                        style={styles.progressSelect}
                      >
                        {progressOptions.map((p) => (
                          <option
                            key={p}
                            value={p}
                            disabled={p < Number(item.progress || 0)}
                          >
                            {p}%
                          </option>
                        ))}
                      </select>

                      <span style={styles.timeText}>
                        {getDateTime(item.updatedAt)}
                      </span>

                      <div
                        style={styles.menuWrap}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          style={styles.menuBtn}
                          onClick={() =>
                            setOpenMenuId(
                              openMenuId === item._id ? null : item._id,
                            )
                          }
                        >
                          <MoreVertical size={18} />
                        </button>

                        {openMenuId === item._id && (
                          <div style={styles.menuBox}>
                            <button
                              type="button"
                              style={styles.menuItem}
                              onClick={() => openUpload(item, "photo")}
                            >
                              Upload Photo
                            </button>

                            <button
                              type="button"
                              style={styles.menuItem}
                              onClick={() => openUpload(item, "video")}
                            >
                              Upload Video
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={styles.progressLineWrap}>
                      <div style={styles.progressLineBg}>
                        <div
                          style={{
                            ...styles.progressLine,
                            width: `${item.progress}%`,
                          }}
                        />
                        <div
                          style={{
                            ...styles.progressCircle,
                            left: `calc(${item.progress}% - 8px)`,
                          }}
                        />
                      </div>

                      <div style={styles.scale}>
                        <span>0%</span>
                        <span>25%</span>
                        <span>50%</span>
                        <span>75%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {selectedItem && (
        <div style={styles.drawerOverlay}>
          <div style={styles.drawer}>
            <button
              type="button"
              style={styles.fixedCloseBtn}
              onClick={() => setSelectedItem(null)}
            >
              ×
            </button>

            <div style={styles.drawerHeader}>
              <h3 style={styles.drawerHeading}>Item Details</h3>
            </div>

            <div style={styles.drawerSection}>
              <div style={styles.drawerTitle}>Basic Details</div>

              <div style={styles.detailTop}>
                <div style={styles.placeholderBox}>
                  <ImagePlus size={52} color="#aaa" />
                </div>

                <div style={styles.detailInfo}>
                  <label>Element Name</label>
                  <p>{selectedItem.name}</p>

                  <label>Element Description</label>
                  <p>{selectedItem.description || "-"}</p>
                </div>
              </div>

              <div style={styles.detailGrid}>
                <div>
                  <label>Element Category</label>
                  <p>{selectedItem.category || "-"}</p>
                </div>

                <div>
                  <label>Element Code</label>
                  <p>{selectedItem.code || "-"}</p>
                </div>

                <div>
                  <label>UOM</label>
                  <p>{selectedItem.uom || "-"}</p>
                </div>

                <div>
                  <label>Quantity</label>
                  <p>{selectedItem.quantity || "-"}</p>
                </div>

                <div>
                  <label>Item Type</label>
                  <p>{selectedItem.itemType || "-"}</p>
                </div>

                <div>
                  <label>Status</label>
                  <p>{selectedItem.status || "-"}</p>
                </div>
              </div>
            </div>

            <div style={styles.drawerSection}>
              <div style={styles.drawerTitle}>Progress</div>

              <div style={styles.methodBox}>
                <p style={styles.smallLabel}>Method of Update</p>

                <label style={styles.radioLabel}>
                  <input type="radio" checked readOnly />
                  Percentage
                </label>

                <label style={styles.radioLabel}>
                  <input type="radio" disabled />
                  Quantity
                </label>
              </div>

              <div style={styles.drawerProgressTop}>
                <span>
                  Progress
                  <br />
                  Status: {selectedItem.progress}%
                </span>

                <span style={styles.timeText}>
                  {getDateTime(selectedItem.updatedAt)}
                </span>
              </div>

              <input
                type="range"
                min="0"
                max="100"
                step="25"
                value={selectedItem.progress}
                onChange={(e) => handleDrawerSliderChange(e.target.value)}
                onMouseUp={(e) => handleDrawerSliderSave(e.target.value)}
                onTouchEnd={(e) => handleDrawerSliderSave(e.target.value)}
                style={styles.drawerRange}
              />

              <div style={styles.scale}>
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>

              <div style={styles.mediaHeader}>
                <span>Image / Video</span>

                <div>
                  <button
                    style={styles.addMediaBtn}
                    onClick={() => openUpload(selectedItem, "photo")}
                  >
                    + Add Image
                  </button>

                  <button
                    style={styles.addMediaBtn}
                    onClick={() => openUpload(selectedItem, "video")}
                  >
                    + Add Video
                  </button>
                </div>
              </div>

              <div style={styles.mediaGrid}>
                {(selectedItem.photos || []).map((photo) => (
                  <a
                    key={photo._id || photo.fileUrl}
                    href={getFileUrl(photo.fileUrl)}
                    target="_blank"
                    rel="noreferrer"
                    style={styles.mediaCard}
                  >
                    <img
                      src={getFileUrl(photo.fileUrl)}
                      alt="photo"
                      style={styles.mediaImg}
                    />
                    <span>{getDateTime(photo.uploadedAt)}</span>
                  </a>
                ))}

                {(selectedItem.videos || []).map((video) => (
                  <a
                    key={video._id || video.fileUrl}
                    href={getFileUrl(video.fileUrl)}
                    target="_blank"
                    rel="noreferrer"
                    style={styles.mediaCard}
                  >
                    <div style={styles.videoThumb}>
                      <Video size={34} />
                    </div>
                    <span>{getDateTime(video.uploadedAt)}</span>
                  </a>
                ))}

                {(selectedItem.photos || []).length === 0 &&
                  (selectedItem.videos || []).length === 0 && (
                    <div style={styles.noMedia}>No photos/videos uploaded</div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f7fb",
    minHeight: "100vh",
    padding: "16px",
    fontFamily: "Inter, Arial, sans-serif",
  },
  header: {
    background: "#fff",
    borderRadius: "12px",
    padding: "14px 18px",
    marginBottom: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  },
  backBtn: {
    border: "none",
    background: "#eef2ff",
    color: "#334155",
    padding: "8px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
    marginBottom: "8px",
  },
  title: {
    margin: 0,
    fontSize: "22px",
    color: "#111827",
  },
  projectName: {
    margin: "4px 0 0",
    color: "#64748b",
    fontSize: "14px",
  },
  actionBtn: {
    background: "#ef233c",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 18px",
    cursor: "pointer",
    fontWeight: 700,
  },
  topTabs: {
    background: "#fff",
    padding: "0 16px",
    borderRadius: "10px",
    display: "flex",
    gap: "20px",
    marginBottom: "14px",
  },
  topTab: {
    padding: "14px 0",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontWeight: 600,
    color: "#1f2937",
    borderBottom: "3px solid transparent",
  },
  activeTopTab: {
    color: "#ef233c",
    borderBottom: "3px solid #ef233c",
  },
  card: {
    background: "#fff",
    borderRadius: "10px",
    border: "1px solid #d7dce3",
    overflow: "hidden",
  },
  roomTabWrapper: {
    display: "flex",
    gap: "14px",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: "1px solid #d7dce3",
    overflowX: "auto",
    whiteSpace: "nowrap",
  },
  roomTab: {
    border: "none",
    background: "transparent",
    padding: "8px 4px",
    fontWeight: 600,
    cursor: "pointer",
    borderBottom: "2px solid transparent",
  },
  activeRoomTab: {
    color: "#ef233c",
    borderBottom: "2px solid #ef233c",
  },
  count: {
    background: "#c7c9cc",
    color: "#fff",
    borderRadius: "7px",
    padding: "2px 6px",
    fontSize: "12px",
    marginLeft: "4px",
  },
  tableHeader: {
    display: "grid",
    gridTemplateColumns: "40px 60px 2.1fr 1fr 0.8fr 0.9fr 0.7fr 0.8fr 3.2fr",
    alignItems: "center",
    padding: "12px 16px 8px",
    fontSize: "13px",
    fontWeight: 700,
  },
  filterRow: {
    display: "grid",
    gridTemplateColumns: "40px 60px 2.1fr 1fr 0.8fr 0.9fr 0.7fr 0.8fr 3.2fr",
    alignItems: "center",
    padding: "0 16px 12px",
    gap: "8px",
    borderBottom: "1px solid #d7dce3",
  },
  input: {
    width: "100%",
    height: "30px",
    border: "none",
    background: "#f1f4f8",
    borderRadius: "6px",
    padding: "0 10px",
    outline: "none",
    fontSize: "13px",
  },
  select: {
    width: "100%",
    height: "30px",
    border: "none",
    background: "#f1f4f8",
    borderRadius: "6px",
    padding: "0 8px",
    outline: "none",
    fontSize: "13px",
  },
  sectionRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "13px 16px",
    borderBottom: "1px solid #d7dce3",
    fontWeight: 700,
  },
  itemRow: {
    display: "grid",
    gridTemplateColumns: "40px 60px 2.1fr 1fr 0.8fr 0.9fr 0.7fr 0.8fr 3.2fr",
    alignItems: "center",
    padding: "16px",
    minHeight: "94px",
    borderBottom: "1px solid #d7dce3",
    fontSize: "14px",
    cursor: "pointer",
  },
  itemName: {
    fontWeight: 700,
    color: "#111827",
    marginBottom: "4px",
  },
  desc: {
    color: "#334155",
    fontSize: "12px",
    maxWidth: "330px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  code: {
    fontSize: "12px",
    color: "#111827",
    marginTop: "3px",
  },
  statusBadge: {
    borderRadius: "7px",
    padding: "5px 8px",
    fontSize: "12px",
    fontWeight: 700,
  },
  typeBadge: {
    border: "1px solid #a78bfa",
    color: "#7c3aed",
    background: "#faf5ff",
    borderRadius: "7px",
    padding: "4px 8px",
    fontSize: "12px",
  },
  progressTop: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#64748b",
    fontSize: "12px",
    marginBottom: "10px",
  },
  progressSelect: {
    height: "28px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    fontSize: "12px",
    padding: "0 6px",
    outline: "none",
    minWidth: "90px",
  },
  timeText: {
    fontSize: "11px",
    color: "#64748b",
    minWidth: "90px",
  },
  menuWrap: {
    position: "relative",
    marginLeft: "auto",
  },
  menuBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
  },
  menuBox: {
    position: "absolute",
    right: 0,
    top: "28px",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "8px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
    zIndex: 50,
    minWidth: "150px",
    overflow: "hidden",
  },
  menuItem: {
    width: "100%",
    padding: "10px 12px",
    border: "none",
    background: "#fff",
    textAlign: "left",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
  },
  progressLineWrap: {
    width: "100%",
  },
  progressLineBg: {
    position: "relative",
    width: "100%",
    height: "6px",
    background: "#e5e7eb",
    borderRadius: "99px",
  },
  progressLine: {
    height: "6px",
    background: "#67cfd5",
    borderRadius: "99px",
  },
  progressCircle: {
    position: "absolute",
    top: "-5px",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    border: "2px solid #cbd5e1",
    background: "#fff",
  },
  scale: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "11px",
    color: "#22b8c3",
    marginTop: "6px",
  },
  empty: {
    padding: "40px",
    textAlign: "center",
    color: "#64748b",
    fontWeight: 600,
  },
  drawerOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 999999,
    display: "flex",
    justifyContent: "flex-end",
  },
  drawer: {
    position: "relative",
    width: "520px",
    height: "100vh",
    background: "#fff",
    overflowY: "auto",
    boxShadow: "-4px 0 20px rgba(0,0,0,0.2)",
  },
  fixedCloseBtn: {
    position: "fixed",
    top: "18px",
    right: "18px",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "none",
    background: "#111827",
    color: "#ffffff",
    fontSize: "26px",
    lineHeight: "40px",
    fontWeight: 700,
    cursor: "pointer",
    zIndex: 1000000,
  },
  drawerHeader: {
    background: "#fff",
    padding: "18px 22px",
    borderBottom: "1px solid #e5e7eb",
  },
  drawerHeading: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 800,
    color: "#111827",
  },
  drawerSection: {
    padding: "18px 22px",
    borderBottom: "1px solid #e5e7eb",
  },
  drawerTitle: {
    fontWeight: 800,
    marginBottom: "14px",
  },
  detailTop: {
    display: "flex",
    gap: "16px",
  },
  placeholderBox: {
    width: "150px",
    height: "150px",
    background: "#eeeeee",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  detailInfo: {
    flex: 1,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    marginTop: "16px",
  },
  smallLabel: {
    color: "#6b7280",
    fontSize: "13px",
  },
  radioLabel: {
    display: "block",
    margin: "8px 0",
    fontSize: "14px",
  },
  methodBox: {
    marginBottom: "16px",
  },
  drawerProgressTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "16px",
    fontSize: "13px",
  },
  drawerRange: {
    width: "100%",
    accentColor: "#67cfd5",
    cursor: "pointer",
  },
  mediaHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "24px",
    marginBottom: "12px",
    fontSize: "14px",
    fontWeight: 700,
  },
  addMediaBtn: {
    border: "none",
    background: "transparent",
    color: "#ef233c",
    cursor: "pointer",
    fontWeight: 700,
    marginLeft: "8px",
  },
  mediaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "10px",
  },
  mediaCard: {
    textDecoration: "none",
    color: "#334155",
    fontSize: "11px",
  },
  mediaImg: {
    width: "100%",
    height: "85px",
    objectFit: "cover",
    borderRadius: "6px",
    background: "#eee",
  },
  videoThumb: {
    width: "100%",
    height: "85px",
    borderRadius: "6px",
    background: "#e5e7eb",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#475569",
  },
  noMedia: {
    gridColumn: "1 / -1",
    padding: "20px",
    background: "#f8fafc",
    color: "#64748b",
    textAlign: "center",
    borderRadius: "8px",
  },
};

export default SiteProgress;
